'use client';

import React, { FC, useState, useEffect, useMemo } from 'react';

import { Document } from '@contentful/rich-text-types';

import classNames from 'classnames';

import AccessibilityLink from '@/components/common/AccessibilityLink/AccessibilityLink';
import RichText from '@/components/common/RichText/RichText';

import { TW_BG_COLORS, TW_TEXT_COLORS } from '@/core/constants';
import { FeatureFlag } from '@/core/featureFlags';
import { TypeGlobalNavFields } from '@/core/generated-types';

import useFeature from '@/hooks/useFeature';
import useLocale from '@/hooks/useLocale';

import { Link } from '@/navigation';

import { buildLinkUrl } from '@/utils/linkBuilder';
import {
  CLICKED_ITEM_TYPES,
  PAGE_LOCATIONS,
  trackItemClickedEvent,
} from '@/utils/rudderstack';
import { shouldShowMessageBanner } from './helpers';
import styles from './MessageBanner.module.scss';

// Cycle interval in milliseconds (default 10 seconds)
const BANNER_CYCLE_INTERVAL_MS = 10000;

// Fade transition duration in milliseconds
const FADE_DURATION_MS = 200;

export const MessageBanner: FC<TypeGlobalNavFields> = ({
  bannerText,
  bannerTextList,
  bannerLinks,
  bannerBackgroundColor,
  bannerTextColor,
  enableAccessibilityLabel = 'Enable Accessibility',
  showAccessibilityLink,
  containerWidth = '1200px',
}) => {
  const locale = useLocale();
  const evergreenMessaging = useFeature(
    FeatureFlag.EVERGREEN_DISCOUNT_MESSAGING,
    'off',
  );

  // When show-messaging: use bannerTextList and cycle logic if it exists.
  // Otherwise (off / suppress-messaging): ignore bannerTextList, use only bannerText.
  // Normalize flag the same way as PDPPromoText/ProductDetailsHeader (case-insensitive).
  const isShowMessaging =
    String(evergreenMessaging).toLowerCase() === 'show-messaging';
  const useBannerTextList =
    isShowMessaging && bannerTextList && bannerTextList.length > 0;

  const bannerTexts = useMemo((): Document[] => {
    if (useBannerTextList) {
      return bannerTextList
        .map((entry) => entry.fields?.content as Document)
        .filter((content): content is Document => !!content);
    }
    return bannerText ? [bannerText as Document] : [];
  }, [useBannerTextList, bannerTextList, bannerText]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  // Clamp currentIndex when bannerTexts changes (e.g. locale or CMS update shortens the list)
  useEffect(() => {
    if (bannerTexts.length === 0) return;
    setCurrentIndex((prev) => Math.min(prev, bannerTexts.length - 1));
  }, [bannerTexts]);

  // Cycle through banner texts
  useEffect(() => {
    if (bannerTexts.length <= 1) return;

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const interval = setInterval(() => {
      // Start fade out
      setIsVisible(false);

      // After fade out completes, change content and fade in
      timeoutId = setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % bannerTexts.length);
        setIsVisible(true);
      }, FADE_DURATION_MS);
    }, BANNER_CYCLE_INTERVAL_MS);

    return () => {
      clearInterval(interval);
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, [bannerTexts.length]);

  const shouldShowBanner = shouldShowMessageBanner({
    bannerText,
    bannerLinks,
    showAccessibilityLink,
  });

  // Also show banner if there is at least one usable banner text
  const hasBannerTextList = bannerTexts.length > 0;

  if (!shouldShowBanner && !hasBannerTextList) {
    return null;
  }

  // Use the color mappings to get complete Tailwind class strings
  // (dynamic class generation like `bg-${color}` doesn't work with Tailwind's purge)
  const bgColorKey = bannerBackgroundColor?.fields
    ?.color as keyof typeof TW_BG_COLORS;
  const textColorKey = bannerTextColor?.fields
    ?.color as keyof typeof TW_TEXT_COLORS;

  const bgColorClass =
    bgColorKey && TW_BG_COLORS[bgColorKey]
      ? TW_BG_COLORS[bgColorKey]
      : 'bg-blue-deep';
  const textColorClass =
    textColorKey && TW_TEXT_COLORS[textColorKey]
      ? TW_TEXT_COLORS[textColorKey]
      : 'text-white';

  const currentBannerText = bannerTexts[currentIndex];

  return (
    <div
      className={classNames('h-[53px]', bgColorClass, textColorClass)}
      data-testid="message-banner"
    >
      <div
        className={`flex max-w-[${containerWidth}] mx-auto px-5 lg:px-4 justify-between items-center h-full`}
      >
        <div
          className={classNames('text-sm pr-4', styles.bannerText)}
          style={{
            opacity: isVisible ? 1 : 0,
            transition: `opacity ${FADE_DURATION_MS}ms ease-in-out`,
          }}
        >
          {currentBannerText && (
            <RichText openLinkSameWindow={true} content={currentBannerText} />
          )}
        </div>
        <div className="flex items-center gap-4">
          {bannerLinks && (
            <div className="items-center gap-4 hidden md:flex">
              {bannerLinks.map((link) => {
                const href = buildLinkUrl(link, locale);

                return (
                  <Link
                    key={link.sys.id}
                    href={href}
                    className="text-sm hover:underline transition-all duration-200"
                    onClick={() => {
                      trackItemClickedEvent({
                        url: window.location.href,
                        type: CLICKED_ITEM_TYPES.LINK,
                        pageLocation: PAGE_LOCATIONS.NAVIGATION,
                        cta: link.fields.linkText || '',
                      });
                    }}
                  >
                    {(link.fields as any).linkText || 'Link'}
                  </Link>
                );
              })}
            </div>
          )}

          {showAccessibilityLink && (
            <AccessibilityLink
              enableAccessibilityLabel={enableAccessibilityLabel}
              className="text-sm underline md:no-underline md:hover:underline transition-all duration-200 text-right lg:text-left"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(MessageBanner);
